// objectives-view.ts — the in-game ObjectivesForm-family DOM overlay (HTML/DOM UI
// shell plan, Phase 5, Forms 1 + 3). Renders an ObjectivesSnapshot on a 160×160
// forms.ini.pp design stage OVER the live canvas, modal (eats all input while up).
// `kind` selects the form:
//   - 'briefing' (default, Form 1, kidfObjectives 1025): read-only, two tabs
//     (BRIEFING / STATISTICS) toggled by the STATISTICS⇄BRIEFING button (view-local
//     state), OK → onClose() resumes. The C++ briefing does NOT zip (preserved
//     m_fAnimate quirk) — but ShellDoModal overwrites it true, so it DOES (see below).
//   - 'win' / 'lose' (Form 3, kidfWinSummary 1010 / kidfLoseSummary 1003): the
//     SAME 4 objective rows + 7 stat rows shown TOGETHER (NO tabs), a RESULT LINE at
//     (80,63) (missionResult), and the win/lose buttons → onChoice(kidc) (routed
//     through the shared post-modal handler). The summaries DO zip + play
//     ksfxGameWin/LoseLevel (the SFX is played by ShowObjectives, not here).
//
// Geometry is the SHIPPED forms.ini.pp form 1025 (FORM 0 0 160 160, FORMBACKCOLOR
// 12, center): MissionTitle (62,6) + "OBJECTIVES" (62,14) center; 4 objective rows
// text (4, 25/34/43/52) left + status (155, …) right; tab header (80,63) center;
// BRIEFING body (4,74,152,69) left multiline; STATISTICS 7 rows y=74..128
// (label x=4, value right x=155); STATISTICS⇄BRIEFING toggle (50,140,60,14) + OK
// (120,140,40,14).
//
// SFX / animation: the briefing form's `m_fAnimate=false` (objectives.ts:128) is a
// PRESERVED DEAD STORE — ShellForm.ShellDoModal(res, /*fAnimate*/true,
// /*fShowSound*/so===ksoObjectives, …) (objectives.ts:282) overwrites m_fAnimate to
// TRUE and arms the form-show sound (shellform.ts:298-348). So the briefing DOES zip
// in — EVERY control cascades to its place from an alternating side (odd→left /
// even→right, m_axSrc shellform.ts:325), staggered kctZipLag=30ms over a kctZip=250ms
// linear slide — and plays ksfxGuiFormShow at the end. This overlay reproduces that
// (the static no-zip form I first built took the dead store at face value).
//
// The design-stage chrome (formScale/makeStage/at/the 9-slice button/formTitle) is the
// SHARED chrome.ts primitives (Phase 2 de-dup); the label/multiline are view-local
// (the objectives form's left/right-aligned body labels). The button uses fontFloor 9.

import type { ObjectivesSnapshot } from './objectives-snapshot.ts';
import type { ButtonArt } from '../title-art.ts';
import { zipInStage } from '../formchrome.ts';
import { kidcRestartMission, kidcAbortMission, kidcLoadGame } from '../../ui/objectives.ts';
import { kidcCancel } from '../../ui/uikit.ts';
import {
  COL_BG,
  COL_FG,
  TEXT_SHADOW,
  el,
  FONT_BODY,
  buttonWidthU,
  makeStage,
  at,
  formTitle,
  makeStageButton,
  type StageButtonSize,
  isPortraitBoost,
} from '../chrome.ts';

/** which form this view renders: the during-play BRIEFING (default — tabs, OK→close)
 *  or a WIN / LOSE RESULTS SUMMARY (no tabs, result line, win/lose buttons →
 *  onChoice). The summary shares objectivesSnapshot + this view with the briefing
 *  (HTML/DOM UI shell plan, Phase 5, Form 3). */
export type ObjectivesViewKind = 'briefing' | 'win' | 'lose';

export interface ObjectivesViewDeps {
  snapshot: ObjectivesSnapshot;
  /** the overlay container (created by the host; this view owns its contents). */
  root: HTMLElement;
  /** which form this view renders (default 'briefing'). */
  kind?: ObjectivesViewKind;
  /** the MULTIPLAYER summary — "YOU WIN!"/"YOU LOSE!" + an OK-only button set
   *  (kidfMultiplayerWin/LoseSummary). Ignored for the briefing. */
  mp?: boolean;
  /** the result-line text for a win/lose summary ("OBJECTIVES COMPLETED!" /
   *  "MISSION FAILED" / "MISSION ABORTED" / "YOU WIN!" / "YOU LOSE!"); rendered at (80,63). */
  missionResult?: string;
  /** BRIEFING only: OK / dismiss → resume the game (host unmounts + calls done()). */
  onClose?: () => void;
  /** WIN/LOSE only: a button press → route the chosen kidc through the shared
   *  post-modal handler (host unmounts the overlay BEFORE running it). */
  onChoice?: (idc: number) => void;
  /** MP WIN/LOSE summary only: offer a "VIEW REPLAY" button. It is not rendered until the server
   *  announces the recording is in R2 (host calls setReplayReady → re-render), then it APPEARS beside OK
   *  and invokes onWatchReplay (opens the standalone viewer). Until then the summary is OK-only — no
   *  greyed/dead button. Absent ⇒ no replay affordance (SP). */
  showReplay?: boolean;
  /** MP only: the recording is already in R2 (the announce raced ahead of the summary) → show it at once. */
  replayReady?: boolean;
  /** MP only: VIEW REPLAY pressed → open the in-browser replay viewer (host wires the URL). */
  onWatchReplay?: () => void;
  /** the title-screen backdrop (titlescreenbkgd.png) as a CSS url; absent ⇒ flat. */
  titleBg?: string;
  /** the raw 9-slice button pieces — faithful chrome; absent ⇒ a CSS-button fallback. */
  buttonArt?: ButtonArt;
  /** play a GUI sfx (ksfxGui*) on a button press, wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
}

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

export class ObjectivesView {
  private readonly d: ObjectivesViewDeps;
  /** the shared 9-slice button renderer (owns its own composite cache; fontFloor 9). */
  private readonly btn: (
    label: string,
    on: () => void,
    size?: StageButtonSize,
  ) => HTMLButtonElement;
  /** the tab toggle — false = BRIEFING, true = STATISTICS. The canvas s_fStatistics
   *  (objectives.ts:91) is a static reset to false for the during-play briefing
   *  (fForceInfoDisplay=true on the SP/credits paths); we start on BRIEFING. */
  private showStatistics = false;
  /** MP summary: has the server announced the recording is in R2? false → OK-only; true → VIEW REPLAY
   *  appears beside OK. Seeded from deps; flipped by setReplayReady (which re-renders). */
  private replayReady: boolean;
  /** the ShellForm zip plays ONCE, on the first show — not on resize/tab re-renders
   *  (the canvas zips the form once, on ShellDoModal). */
  private animated = false;
  private formShowTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(d: ObjectivesViewDeps) {
    this.d = d;
    this.replayReady = d.replayReady === true;
    const mk = makeStageButton(d);
    this.btn = (label, on, size) => mk(label, on, size, { fontFloor: 9 });
    const s = d.root.style;
    // a fixed, full-window MODAL overlay above #screen (z above the canvas at 10);
    // pointer-events:all so the canvas behind it gets nothing while it is up.
    s.position = 'fixed';
    s.inset = '0';
    s.display = 'flex';
    s.alignItems = 'center';
    s.justifyContent = 'center';
    s.zIndex = '20';
    s.color = COL_FG;
    s.fontFamily = FONT;
    s.userSelect = 'none';
    s.pointerEvents = 'all';
    s.overflow = 'hidden'; // clip controls parked off-window during the zip (no scrollbar flash)
    // EVERY shell form renders over the title backdrop with a black letterbox
    // (shellform.ts OnPaintBackground) — the objectives form is FORMBACKCOLOR 12.
    s.backgroundImage = d.titleBg !== undefined ? `url(${d.titleBg})` : 'none';
    s.backgroundColor = COL_BG;
    s.backgroundSize = 'cover';
    s.backgroundPosition = 'center';
    s.backgroundRepeat = 'no-repeat';
    window.addEventListener('resize', this.onResize);
    this.render();
  }

  private readonly onResize = (): void => {
    this.render();
  };

  /** detach the resize listener + cancel a pending form-show sfx (the host unmounts
   *  the overlay on OK). */
  destroy(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.formShowTimer !== null) {
      clearTimeout(this.formShowTimer);
      this.formShowTimer = null;
    }
  }

  /** The host received the server's ScReplayAvailable — make the MP summary's VIEW REPLAY button APPEAR
   *  (re-renders WITHOUT re-zipping; the canvas zipped once). No-op if already shown or not a replay
   *  summary. */
  setReplayReady(): void {
    if (this.replayReady || this.d.showReplay !== true) return;
    this.replayReady = true;
    this.render();
  }

  // the ShellForm zip-in — the SAME faithful cascade the SP menu / MP lobby use
  // (formchrome.ts zipInStage ↔ shellform.ts:298-384): EVERY control flies to its
  // place from an alternating side, staggered, then ksfxGuiFormShow. The objectives
  // form is mostly labels, so cascading ALL controls (not just buttons) matters here.
  private zipIn(stage: HTMLElement): void {
    if (this.formShowTimer !== null) clearTimeout(this.formShowTimer);
    this.formShowTimer = zipInStage(stage, this.d.playSfx);
  }

  // ── the faithful design stage (forms.ini: the objectives form is 160×160) ─────
  // The SAME fixed magnification as the SP menu/MP lobby (chrome.ts formScale/makeStage).
  // a button placed at forms.ini design coords (x, y) with a w×h-unit composited
  // bitmap (the objectives form's buttons are 14u tall).
  private stageButton(
    s: number,
    label: string,
    x: number,
    y: number,
    w: number,
    h: number,
    on: () => void,
  ): HTMLElement {
    return at(this.btn(label, on, { w: w * s, h: h * s }), s, x, y);
  }

  // a bottom BUTTON ROW: each button SIZED to its label via the shared buttonWidthU (the
  // same cap-aware sizer the rest of the UI uses — so every label keeps its full margin
  // inside the 9-slice frame), the row CENTRED on the band centre. On the narrow 160-wide
  // form the 3-button row (RETRY/ABORT/LOAD SAVED) measures wider than the form, so it
  // extends SYMMETRICALLY into the surrounding backdrop (the stage is overflow-visible and
  // the formScale leaves room within the window) — keeping each button's smart-width
  // rather than shrinking the row + starving the long label's padding ("LOAD SAVED" was
  // left with ~3-6px while RETRY had ~14px). (The objectives buttons are 14u tall.)
  private buttonRow(
    stage: HTMLElement,
    s: number,
    y: number,
    h: number,
    bandX: number,
    bandW: number,
    btns: { label: string; on: () => void }[],
  ): void {
    const gapU = 5;
    const gaps = gapU * (btns.length - 1);
    let hh = h;
    let sized = btns.map((b) => ({ ...b, w: buttonWidthU(b.label, hh, s) }));
    let buttonsW = sized.reduce((a, b) => a + b.w, 0);
    // PHONE-PORTRAIT: the 160u form nearly fills the viewport, so a row wider than the form
    // (RETRY/ABORT/LOAD SAVED) no longer has backdrop room to extend into — it clipped at
    // both edges. Shrink the buttons (height drives the 9-slice label font, so the labels
    // shrink with them — no clipped text) so the row fits the band. Landscape keeps the
    // authored smart widths and extends symmetrically into the backdrop (there IS room).
    if (isPortraitBoost() && buttonsW + gaps > bandW) {
      const f = (bandW - gaps) / buttonsW;
      hh *= f;
      sized = sized.map((b) => ({ ...b, w: b.w * f }));
      buttonsW = bandW - gaps;
    }
    const total = buttonsW + gaps;
    let x = bandX + (bandW - total) / 2; // centred on the band centre
    for (const b of sized) {
      stage.append(this.stageButton(s, b.label, x, y, b.w, hh, b.on));
      x += b.w + gapU;
    }
  }

  private get kind(): ObjectivesViewKind {
    return this.d.kind ?? 'briefing';
  }

  render(): void {
    const { stage, s } = makeStage();
    const snap = this.d.snapshot;
    const summary = this.kind !== 'briefing';

    // MissionTitle (62,6,35) center + the second title line (62,14,35) center —
    // both kifntTitle. The briefing's second line is "OBJECTIVES"; the WIN/LOSE
    // SUMMARY's is "PERFORMANCE REVIEW" (forms.ini.pp kidfWin/LoseSummary:416/446).
    // The authored width is 35 but the labels are centered on the form, so render
    // them spanning the form width centered (the canvas centers a too-wide label on
    // its authored anchor; full-width keeps it on-form).
    stage.append(at(formTitle(snap.missionTitle, s), s, 0, 6, 160));
    stage.append(at(formTitle(summary ? 'PERFORMANCE REVIEW' : 'OBJECTIVES', s), s, 0, 14, 160));

    // the 4 objective rows: text (4, 25/34/43/52) left + status (155, …) right. The authored
    // labels are auto-width (forms.ini.pp `(4 y 0 0)` / `(155 y 0 0) right`), so a long
    // objective text ran INTO the right-aligned status ("…counterattack" + "Incomplete" →
    // "counterattackIncomplete"). Lay each row as ONE flex line: the text takes the remaining
    // width and ellipsises, the status keeps its natural width pinned right at x=155 — so they
    // can never collide. (Spans x=4..155, the authored anchors.)
    const rowY = [25, 34, 43, 52];
    for (let i = 0; i < snap.objectives.length; i++) {
      const o = snap.objectives[i];
      if (o.text !== '' || o.status !== '')
        stage.append(at(this.objectiveRow(o.text, o.status, s), s, 4, rowY[i], 151));
    }

    if (summary) {
      // the RESULT LINE at (80,63) center kifntTitle — kidcMissionResult, where the
      // briefing puts its tab header (forms.ini.pp kidfWinSummary:425). The SUMMARY
      // shows the 4 objective rows AND the 7 stat rows TOGETHER (no BRIEFING/STATS
      // tabs, no toggle).
      const resultEl = formTitle(this.d.missionResult ?? '', s);
      resultEl.dataset.summaryResult = '1'; // automation marker (wiUi.summaryResult)
      stage.append(at(resultEl, s, 0, 63, 160));
      this.renderStatistics(stage, s);
      this.renderSummaryButtons(stage, s);
    } else {
      // the tab header at (80,63) center — "BRIEFING" or "STATISTICS".
      stage.append(
        at(formTitle(this.showStatistics ? 'STATISTICS' : 'BRIEFING', s), s, 0, 63, 160),
      );
      if (this.showStatistics) {
        this.renderStatistics(stage, s);
      } else {
        // the BRIEFING body — the objective-info multiline (4,74,152,69) left.
        stage.append(at(this.multiline(snap.briefing, s), s, 4, 74, 152, 69));
      }
      // STATISTICS⇄BRIEFING toggle + OK — each SIZED to its label via the shared buttonWidthU
      // (the wide WI face crowded "STATISTICS" against the 9-slice frame at the fixed width),
      // then CENTRED as a row on the 160-wide form. The toggle shows the OTHER tab's name.
      this.buttonRow(stage, s, 140, 14, 0, 160, [
        {
          label: this.showStatistics ? 'BRIEFING' : 'STATISTICS',
          on: () => {
            this.showStatistics = !this.showStatistics;
            this.render();
          },
        },
        { label: 'OK', on: () => this.d.onClose?.() },
      ]);
    }

    this.d.root.replaceChildren(stage);
    // The ShellForm zip + ksfxGuiFormShow play on the FIRST show only (ShellDoModal
    // fAnimate=true; the Init m_fAnimate=false is a dead store). Resize / tab-toggle
    // re-renders rebuild the stage WITHOUT re-zipping (the canvas zips once). The
    // win/lose summaries DO zip in too (the same ShellDoModal fAnimate=true path).
    if (!this.animated) {
      this.animated = true;
      this.zipIn(stage);
    }
  }

  // the WIN/LOSE RESULTS-SUMMARY buttons (forms.ini.pp kidfWin/LoseSummary:440/470-472).
  // WIN → OK (kidcCancel, 80,140,40,14); LOSE → RETRY (kidcRestartMission) /
  // ABORT (kidcAbortMission) / LOAD SAVED (kidcLoadGame). Each routes the kidc
  // through onChoice (the shared post-modal handler).
  private renderSummaryButtons(stage: HTMLElement, s: number): void {
    const choose = (idc: number): void => {
      this.d.onChoice?.(idc);
    };
    // MP win/lose: OK shows immediately; VIEW REPLAY APPEARS beside it only once the server announces the
    // recording is stored (setReplayReady → re-render). No greyed/pending button: the recording isn't
    // addressable until the WHOLE game is won, so for the winner / a 1v1 final loser (present at the true
    // end) the key arrives within ~a second and VIEW REPLAY pops in; a player knocked out earlier in a
    // multi-player game dismisses this summary to spectate, so their path to the replay is the leaderboard's
    // recent-games list — not a dead button here. Nothing overlaps the stats above.
    if (this.d.showReplay === true) {
      if (this.replayReady) {
        this.buttonRow(stage, s, 140, 14, 0, 160, [
          { label: 'VIEW REPLAY', on: () => this.d.onWatchReplay?.() },
          { label: 'OK', on: () => choose(kidcCancel) },
        ]);
      } else {
        const wOk = buttonWidthU('OK', 14, s);
        stage.append(
          this.stageButton(s, 'OK', (160 - wOk) / 2, 140, wOk, 14, () => choose(kidcCancel)),
        );
      }
      return;
    }
    // MP win+lose are OK-only (kidfMultiplayerWin/LoseSummary); SP win = OK; SP lose =
    // RETRY/ABORT/LOAD SAVED.
    if (this.d.mp === true || this.kind === 'win') {
      // a SINGLE OK button — SIZED to its label via the shared buttonWidthU then horizontally
      // CENTRED on the 160-wide form (a deliberate divergence from the canvas's off-centre x=80
      // for the single-button case, per the user).
      const wOk = buttonWidthU('OK', 14, s);
      stage.append(
        this.stageButton(s, 'OK', (160 - wOk) / 2, 140, wOk, 14, () => choose(kidcCancel)),
      );
    } else {
      // RETRY / ABORT / LOAD SAVED — each SIZED to its label via the shared buttonWidthU
      // ("LOAD SAVED" is the long one; fixed widths clipped it against the 9-slice frame),
      // then CENTRED as a row on the 160-wide form with even gaps.
      this.buttonRow(stage, s, 140, 14, 0, 160, [
        { label: 'RETRY', on: () => choose(kidcRestartMission) },
        { label: 'ABORT', on: () => choose(kidcAbortMission) },
        { label: 'LOAD SAVED', on: () => choose(kidcLoadGame) },
      ]);
    }
  }

  // the 7 STATISTICS rows (forms.ini.pp): label at x=4, value right-aligned at
  // x=155, y=74..128. The label literals are the shipped strings.
  private renderStatistics(stage: HTMLElement, s: number): void {
    const st = this.d.snapshot.statistics;
    const rows: [string, string, number][] = [
      ['Enemy units destroyed', String(st.enemyMobileUnitsKilled), 74],
      ['Enemy buildings destroyed', String(st.enemyStructuresKilled), 83],
      ['Friendly units lost', String(st.mobileUnitsLost), 92],
      ['Friendly buildings lost', String(st.structuresLost), 101],
      ['Credits acquired / spent', st.creditsAcquiredSpent, 110],
      ['Elapsed time', st.elapsedTime, 119],
      ['Title', st.rankTitle, 128],
    ];
    for (const [label, value, y] of rows) {
      stage.append(at(this.label(label, 'left', s), s, 4, y, 110));
      stage.append(at(this.label(value, 'right', s), s, 4, y, 151));
    }
  }

  // ── widgets (the view-local body labels; the chrome is chrome.ts primitives) ──
  // a single-line label (the canvas LabelControl), left- or right-aligned, scaled
  // to the design stage.
  private label(text: string, align: 'left' | 'right', s: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      color: COL_FG,
      textShadow: shadow,
      textAlign: align,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    e.textContent = text;
    return e;
  }

  // one objective row: the objective text (fills the remaining width, ellipsised) + the
  // status pinned to the right edge. A flex line so the two can never overlap (the canvas
  // labels are auto-width and DO overlap on long text).
  private objectiveRow(text: string, status: string, s: number): HTMLElement {
    const row = el('div', {
      display: 'flex',
      alignItems: 'baseline',
      width: '100%',
      fontSize: `${Math.round(5.5 * s)}px`,
    });
    const t = el('div', {
      fontFamily: FONT,
      color: COL_FG,
      textShadow: shadow,
      textAlign: 'left',
      flex: '1 1 auto',
      minWidth: '0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    });
    t.textContent = text;
    const st = el('div', {
      fontFamily: FONT,
      color: COL_FG,
      textShadow: shadow,
      textAlign: 'right',
      flex: '0 0 auto',
      whiteSpace: 'nowrap',
      paddingLeft: `${4 * s}px`,
    });
    st.textContent = status;
    row.append(t, st);
    return row;
  }

  // a multiline label (the canvas LabelControl 'multiline' — the BRIEFING body).
  private multiline(text: string, s: number): HTMLElement {
    const e = el('div', {
      fontFamily: FONT,
      fontSize: `${Math.round(5.5 * s)}px`,
      lineHeight: '1.35',
      color: COL_FG,
      textShadow: shadow,
      textAlign: 'left',
      whiteSpace: 'pre-wrap',
      overflowY: 'auto',
    });
    e.textContent = text;
    return e;
  }
}
